import { Field, Input } from "@headlessui/react";
import clsx from "clsx";

/**
 * Props interface for the InputField component.
 */
interface InputFieldProps extends React.ComponentProps<typeof Input> {
  /** Optional error message to display above the input */
  error?: string | undefined;

  /** The type of the input field */
  type: string;

  /** Optional ref to forward to the input element */
  ref?: React.Ref<HTMLElement>;

  /** The name of the input field */
  name: string; // added by register method from react-hook-form

  /** Optional placeholder text for the input */
  placeholder?: string;
}

/**
 * A reusable input field component that provides consistent input styling and behavior.
 *
 * Features:
 * - Standard input field styling
 * - Error state handling with visual feedback
 * - Proper accessibility with Headless UI components
 * - Responsive design with Tailwind CSS
 * - Designed to work inside FormContainer
 *
 * @example
 * ```tsx
 * <InputField
 *   type="text"
 *   placeholder="Enter your name"
 *   error="Please enter a valid name"
 *   onChange={(e) => setName(e.target.value)}
 * />
 * ```
 */
export default function InputField({ error, ref, ...props }: InputFieldProps) {
  return (
    <Field className="relative">
      {/* Error message with proper spacing */}
      {error && (
        <div className="mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600 font-medium" data-testid="field-error">
            {error}
          </p>
        </div>
      )}
      <div className="relative">
        <Input
          {...props}
          {...(ref && { ref })}
          className={clsx(
            "w-full px-3 py-2 border-2 rounded-lg bg-white text-gray-600 font-mono font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-base",
            {
              "border-red-500": error,
              "border-gray-300": !error,
            }
          )}
          data-testid="input-field"
        />
      </div>
    </Field>
  );
}
